<template>
    <NavigationGrid v-if="gridMenu"
                    :menu="gridMenu" />
    <NavigationArticle v-else-if="articleMenu"
                       :image_url="articleMenu.image_url"
                       :title="articleMenu.title"
                       :subtitle="articleMenu.subtitle"
                       :url="articleMenu.url"
                       :subtype="articleMenu.subtype" />
    <NavigationImage v-else-if="imageMenu"
                     :badge="imageMenu.badge"
                     :button_title="imageMenu.button_title"
                     :url="imageMenu.url"
                     :image_url="imageMenu.image_url"
                     :title="imageMenu.title"
                     :image_title="imageMenu.image_title" />
    <NavigationReview v-else-if="reviewMenu"
                      :name="reviewMenu.name"
                      :url="reviewMenu.url"
                      :score="reviewMenu.score"
                      :logo_url="reviewMenu.logo_url" />
    <NavigationCoupon v-else-if="couponMenu"
                      :title="couponMenu.title"
                      :subtitle="couponMenu.subtitle"
                      :verified="couponMenu.verified"
                      :url="couponMenu.url"
                      :logo_url="couponMenu.logo_url"
                      :rating="couponMenu.rating" />
    <NavigationCompare v-else-if="compareMenu"
                       :url="compareMenu.url"
                       :compared="compareMenu.compared"
                       :comparing="compareMenu.comparing" />
    <NavigationPrice v-else-if="priceMenu"
                     :title="priceMenu.title"
                     :price_change="priceMenu.price_change"
                     :url="priceMenu.url"
                     :image_url="priceMenu.image_url"
                     :price="priceMenu.price" />
    <NavigationBuyItem v-if="buyItemMenu"
                       :change="buyItemMenu.change"
                       :image_url="buyItemMenu.image_url"
                       :title="buyItemMenu.title"
                       :price="buyItemMenu.price"
                       :url="buyItemMenu.url"
                       :button_text="buyItemMenu.button_text" />
    <NavigationDiagram v-else-if="diagramMenu"
                       :title="diagramMenu.title"
                       :data="diagramMenu.data" />
    <NavigationButton v-else-if="buttonMenu"
                      :url="buttonMenu.url"
                      :action="buttonMenu.action"
                      :title="buttonMenu.title"
                      :subtype="buttonMenu.subtype" />
    <NavigationHeading v-else-if="headingMenu && menuStore.isDesktop"
                       :title="headingMenu.title" />
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import NavigationImage from '../data-menus/NavigationImage.vue';
    import NavigationArticle from '../data-menus/NavigationArticle.vue';
    import NavigationButton from '../data-menus/NavigationButton.vue';
    import NavigationReview from '../data-menus/NavigationReview.vue';
    import NavigationCoupon from '../data-menus/NavigationCoupon.vue';
    import NavigationHeading from '../data-menus/NavigationHeading.vue';
    import NavigationGrid from '../data-menus/NavigationGrid.vue';
    import NavigationCompare from '../data-menus/NavigationCompare.vue';
    import NavigationDiagram from '../data-menus/NavigationDiagram.vue';
    import NavigationPrice from '../data-menus/NavigationDetail.vue';
    import { MenuTypeEnum } from '../enums/menuEnums';
    import type {
        INavigationArticle,
        INavigationButton,
        INavigationBuyItem,
        INavigationCompare,
        INavigationCoupon,
        INavigationDetail,
        INavigationDiagram,
        INavigationGrid,
        INavigationHeading,
        INavigationImage,
        INavigationReview,
        TDataMenusWithoutItem,
    } from '../types/menuTypes';
    import type { StringValues } from '@/js/types/helpers';
    import NavigationBuyItem from '../data-menus/NavigationBuyItem.vue';
    import { useMenuStore } from '@/js/store/menu/MenuStore';

    const props = defineProps<{
        menu: TDataMenusWithoutItem,
    }>();

    const menuStore = useMenuStore();

    const isType = <T extends TDataMenusWithoutItem>(
    menu: TDataMenusWithoutItem,
    type: StringValues<MenuTypeEnum>,
        ): menu is T extends TDataMenusWithoutItem ? T : never => menu.type === type;

    const isGrid = (menu: TDataMenusWithoutItem): menu is INavigationGrid => isType<INavigationGrid>(menu, MenuTypeEnum.GRID);
    const isArticle = (menu: TDataMenusWithoutItem): menu is INavigationArticle => isType<INavigationArticle>(menu, MenuTypeEnum.ARTICLE);
    const isImage = (menu: TDataMenusWithoutItem): menu is INavigationImage => isType<INavigationImage>(menu, MenuTypeEnum.IMAGE);
    const isReview = (menu: TDataMenusWithoutItem): menu is INavigationReview => isType<INavigationReview>(menu, MenuTypeEnum.REVIEW);
    const isCoupon = (menu: TDataMenusWithoutItem): menu is INavigationCoupon => isType<INavigationCoupon>(menu, MenuTypeEnum.COUPON);
    const isCompare = (menu: TDataMenusWithoutItem): menu is INavigationCompare => isType<INavigationCompare>(menu, MenuTypeEnum.COMPARE);
    const isPrice = (menu: TDataMenusWithoutItem): menu is INavigationDetail => isType<INavigationDetail>(menu, MenuTypeEnum.DETAIL);
    const isBuyItem = (menu: TDataMenusWithoutItem): menu is INavigationBuyItem => isType<INavigationBuyItem>(menu, MenuTypeEnum.BUY_PAGE);
    const isDiagram = (menu: TDataMenusWithoutItem): menu is INavigationDiagram => isType<INavigationDiagram>(menu, MenuTypeEnum.DIAGRAM);
    const isHeading = (menu: TDataMenusWithoutItem): menu is INavigationHeading => isType<INavigationHeading>(menu, MenuTypeEnum.HEADING);
    const isButton = (menu: TDataMenusWithoutItem): menu is INavigationButton => isType<INavigationButton>(menu, MenuTypeEnum.BUTTON);

    const gridMenu = computed(() => (isGrid(props.menu) ? props.menu : null));
    const articleMenu = computed(() => (isArticle(props.menu) ? props.menu : null));
    const imageMenu = computed(() => (isImage(props.menu) ? props.menu : null));
    const reviewMenu = computed(() => (isReview(props.menu) ? props.menu : null));
    const couponMenu = computed(() => (isCoupon(props.menu) ? props.menu : null));
    const compareMenu = computed(() => (isCompare(props.menu) ? props.menu : null));
    const priceMenu = computed(() => (isPrice(props.menu) ? props.menu : null));
    const buyItemMenu = computed(() => (isBuyItem(props.menu) ? props.menu : null));
    const diagramMenu = computed(() => (isDiagram(props.menu) ? props.menu : null));
    const headingMenu = computed(() => (isHeading(props.menu) ? props.menu : null));
    const buttonMenu = computed(() => (isButton(props.menu) ? props.menu : null));
</script>
